import { Keys, localStorageSync, rehydrateApplicationState } from 'ngrx-store-localstorage';
import { Action, ActionReducer, MetaReducer } from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { AppState } from '../index';
import { CartState, initialState as initialCartState } from '../cart/cart.state';
import { UserState, initialState as initialUserState } from '../user/user.state';
import { setCurrentUser, startUserSession } from '../user/user.actions';
import { ContextState, initialState as initialContextState } from '../../core/context/store/context.state';
import { STATE_OWNER_STORAGE_KEY } from '@shared/consts/state-owner-storage-key';

const PERSISTED_SLICE_KEYS = ['cart', 'user', 'context'] as const;
const keys: Keys = [
  {
    [PERSISTED_SLICE_KEYS[0]]: ['cartId', 'cart', 'shortInfo', 'markupSettings', 'loading', 'markupLoading'],
  },
  PERSISTED_SLICE_KEYS[1],
  PERSISTED_SLICE_KEYS[2],
];
const PREFIX: string = `${environment.companyName}_`;
const storageKeySerializer = (key: string): string => `${PREFIX}${key}`;

interface PersistedSlices {
  cart: CartState;
  context?: ContextState;
  user: UserState;
}

type PersistedState = Partial<PersistedSlices>;

function localStorageSyncReducer<State extends PersistedSlices>(reducer: ActionReducer<State, Action>): ActionReducer<State, Action> {
  let resolvedOwnerId: string | null = null;
  let shouldRestoreContext = true;

  const syncedReducer = localStorageSync({
    keys,
    rehydrate: false,
    storageKeySerializer,
    syncCondition: () => resolvedOwnerId !== null && localStorage.getItem(STATE_OWNER_STORAGE_KEY) === resolvedOwnerId,
  })(reducer) as ActionReducer<State, Action>;

  return (state, action) => {
    if (isStartUserSessionAction(action)) {
      resolvedOwnerId = null;
      shouldRestoreContext = false;

      return syncedReducer(resetPersistedState(state), action);
    }

    const storedOwnerId = localStorage.getItem(STATE_OWNER_STORAGE_KEY);
    let nextState = state;

    const ownerChangedExternally = resolvedOwnerId !== null && storedOwnerId !== resolvedOwnerId;

    if (ownerChangedExternally) {
      nextState = resetPersistedState(state);

      if (!isSetCurrentUserAction(action)) {
        return syncedReducer(nextState, action);
      }

      if (action.user.userId !== storedOwnerId) {
        return nextState as State;
      }

      resolvedOwnerId = null;
    }

    if (!isSetCurrentUserAction(action)) {
      return syncedReducer(nextState, action);
    }

    const currentOwnerId = action.user.userId;
    const persistedState = readPersistedState();
    const persistedOwnerId = persistedState.user?.user?.userId;
    const canRestorePersistedState = storedOwnerId === currentOwnerId || (storedOwnerId === null && persistedOwnerId === currentOwnerId);

    if (resolvedOwnerId === null && canRestorePersistedState) {
      nextState = mergePersistedState(nextState, persistedState, shouldRestoreContext);
    } else if (resolvedOwnerId !== currentOwnerId || storedOwnerId !== currentOwnerId) {
      clearPersistedState();
      nextState = resetPersistedState(nextState);
    }

    localStorage.setItem(STATE_OWNER_STORAGE_KEY, currentOwnerId);
    resolvedOwnerId = currentOwnerId;
    shouldRestoreContext = true;

    return syncedReducer(nextState, action);
  };
}

function readPersistedState(): PersistedState {
  return rehydrateApplicationState({
    keys,
    storage: localStorage,
    storageKeySerializer,
    restoreDates: true,
  }) as PersistedState;
}

function isStartUserSessionAction(action: Action): action is ReturnType<typeof startUserSession> {
  return action.type === startUserSession.type;
}

function isSetCurrentUserAction(action: Action): action is ReturnType<typeof setCurrentUser> {
  return action.type === setCurrentUser.type;
}

function mergePersistedState<State extends PersistedSlices>(
  state: State | undefined,
  persistedState: PersistedState,
  shouldRestoreContext: boolean,
): State | undefined {
  if (!state) {
    return state;
  }

  return {
    ...state,
    cart: { ...state.cart, ...persistedState.cart },
    user: { ...state.user, ...persistedState.user },
    ...(shouldRestoreContext && state.context && persistedState.context
      ? { context: { ...state.context, ...persistedState.context } }
      : {}),
  };
}

function resetPersistedState<State extends PersistedSlices>(state: State | undefined): State | undefined {
  if (!state) {
    return state;
  }

  return {
    ...state,
    cart: { ...initialCartState },
    user: { ...initialUserState },
    ...(state.context ? { context: { ...initialContextState } } : {}),
  };
}

function clearPersistedState(): void {
  PERSISTED_SLICE_KEYS.forEach(key => localStorage.removeItem(storageKeySerializer(key)));
}

export const metaStorageReducers: MetaReducer<AppState, Action>[] = [localStorageSyncReducer];
